import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sun, Moon, Star, Lightbulb, CheckCircle, ChevronRight, Play, Volume2 } from 'lucide-react';
import { Card } from '../components/Card';
interface EmotionPaletteModuleProps {
  onBack: () => void;
}
// Audio helper - play chord
function playChord(frequencies: number[], type: 'major' | 'minor' = 'major') {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  frequencies.forEach((freq, i) => {
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, audioContext.currentTime);
    osc.connect(gain);
    gain.connect(audioContext.destination);
    gain.gain.setValueAtTime(0.15, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1.5);
    osc.start(audioContext.currentTime);
    osc.stop(audioContext.currentTime + 1.5);
  });
}
// Major chord: root, major 3rd, perfect 5th
// Minor chord: root, minor 3rd, perfect 5th
const majorChord = [261.63, 329.63, 392]; // C-E-G
const minorChord = [261.63, 311.13, 392]; // C-Eb-G
function playMelody(notes: number[], interval: number = 400) {
  notes.forEach((freq, i) => {
    setTimeout(() => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, audioContext.currentTime);
      osc.connect(gain);
      gain.connect(audioContext.destination);
      gain.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      osc.start(audioContext.currentTime);
      osc.stop(audioContext.currentTime + 0.4);
    }, i * interval);
  });
}
export function EmotionPaletteModule({
  onBack
}: EmotionPaletteModuleProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [score, setScore] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);
  const lessonSteps = [{
    type: 'learn',
    title: '什么是大调和小调？',
    icon: '🎨',
    content: {
      definition: '大调和小调是音乐中两种基本的"调式"，它们决定了音乐的情绪色彩。就像画画用不同的颜色表达不同的心情一样！',
      keyPoints: ['大调：听起来明亮、开心、阳光', '小调：听起来柔和、忧伤、神秘', '同一首歌用大调和小调演奏，感觉完全不同'],
      example: '生日快乐歌是大调的，听起来很欢快；而很多古风音乐用小调，听起来很有意境。'
    }
  }, {
    type: 'compare',
    title: '听一听，比一比',
    icon: '👂',
    content: {
      instruction: '点击下方按钮，感受大调和小调的区别',
      comparisons: [{
        name: '大调和弦',
        desc: '明亮、开心、像阳光',
        icon: '☀️',
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        bgColor: 'bg-amber-50',
        chord: majorChord,
        type: 'major'
      }, {
        name: '小调和弦',
        desc: '柔和、忧伤、像月光',
        icon: '🌙',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        bgColor: 'bg-purple-50',
        chord: minorChord,
        type: 'minor'
      }]
    }
  }, {
    type: 'emotion',
    title: '情绪与调式',
    icon: '😊',
    content: {
      instruction: '不同的调式表达不同的情绪，点击查看并聆听',
      emotions: [{
        emoji: '😄',
        name: '开心',
        desc: '大调 - 明快跳跃',
        melody: [261.63, 329.63, 392, 523.25],
        type: 'major'
      }, {
        emoji: '😢',
        name: '忧伤',
        desc: '小调 - 低沉缓慢',
        melody: [261.63, 311.13, 349.23, 392],
        type: 'minor'
      }, {
        emoji: '🎉',
        name: '兴奋',
        desc: '大调 - 快速上行',
        melody: [261.63, 293.66, 329.63, 349.23, 392, 440, 493.88, 523.25],
        type: 'major'
      }, {
        emoji: '😌',
        name: '平静',
        desc: '小调 - 舒缓流动',
        melody: [392, 349.23, 311.13, 261.63],
        type: 'minor'
      }]
    }
  }, {
    type: 'quiz',
    title: '听音辨调',
    icon: '❓',
    content: {
      question: '听一段音乐，判断它是大调还是小调？',
      quizzes: [{
        melody: [261.63, 329.63, 392, 329.63, 261.63],
        answer: 0,
        type: 'major'
      }, {
        melody: [261.63, 311.13, 349.23, 311.13, 261.63],
        answer: 1,
        type: 'minor'
      }, {
        melody: [392, 440, 493.88, 523.25],
        answer: 0,
        type: 'major'
      }, {
        melody: [392, 349.23, 311.13, 261.63],
        answer: 1,
        type: 'minor'
      }]
    }
  }, {
    type: 'interactive',
    title: '调色盘体验',
    icon: '🎹',
    content: {
      instruction: '切换大调和小调，感受同一段旋律的情绪变化',
      baseMelody: [261.63, 293.66, 329.63, 392, 329.63, 293.66, 261.63]
    }
  }, {
    type: 'summary',
    title: '知识总结',
    icon: '📝',
    content: {
      points: [{
        emoji: '☀️',
        text: '大调：明亮开心，像阳光普照，常用于欢快的歌曲'
      }, {
        emoji: '🌙',
        text: '小调：柔和忧伤，像月光静谧，常用于抒情的歌曲'
      }, {
        emoji: '🎨',
        text: '调式就像音乐的"颜色"，决定了音乐的情绪'
      }, {
        emoji: '🎵',
        text: '同一首歌用不同调式演奏，感觉完全不同'
      }]
    }
  }];
  const currentLesson = lessonSteps[currentStep];
  const [mode, setMode] = useState<'major' | 'minor'>('major');
  const handleNextStep = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizIndex(0);
    if (currentStep < lessonSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const renderStepContent = () => {
    switch (currentLesson.type) {
      case 'learn':
        const learnContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-35">
            <Card className="p-6 bg-orange-50 border-orange-100" data-id="element-36">
              <div className="flex items-start space-x-4" data-id="element-37">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" data-id="element-38">
                  💡
                </div>
                <div data-id="element-39">
                  <h4 className="font-bold text-orange-800 mb-2" data-id="element-40">定义</h4>
                  <p className="text-orange-700" data-id="element-41">{learnContent.definition}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white" data-id="element-42">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center" data-id="element-43">
                <Lightbulb className="mr-2 text-amber-500" size={20} data-id="element-44" />
                重要知识点
              </h4>
              <div className="space-y-3" data-id="element-45">
                {learnContent.keyPoints.map((point: string, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.2
              }} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg" data-id="element-46">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={20} data-id="element-47" />
                    <span className="text-slate-700" data-id="element-48">{point}</span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-100" data-id="element-49">
              <h4 className="font-bold text-amber-800 mb-2" data-id="element-50">🌟 生活中的例子</h4>
              <p className="text-amber-700" data-id="element-51">{learnContent.example}</p>
            </Card>
          </div>;
      case 'compare':
        const compareContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-52">
            <Card className="p-6 bg-slate-50 border-slate-100" data-id="element-53">
              <p className="text-slate-700 font-medium text-center" data-id="element-54">
                {compareContent.instruction}
              </p>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-55">
              {compareContent.comparisons.map((item: any, i: number) => <motion.button key={i} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => playChord(item.chord, item.type)} className={`p-8 rounded-2xl border-2 ${item.color} text-center transition-all hover:shadow-xl`} data-id="element-56">
                  <div className="text-6xl mb-4" data-id="element-57">{item.icon}</div>
                  <h4 className="font-bold text-xl mb-2" data-id="element-58">{item.name}</h4>
                  <p className="text-sm opacity-80 mb-4" data-id="element-59">{item.desc}</p>
                  <div className="flex items-center justify-center space-x-2" data-id="element-60">
                    <Play size={20} fill="currentColor" data-id="element-61" />
                    <span className="font-medium" data-id="element-62">点击聆听</span>
                  </div>
                </motion.button>)}
            </div>

            <Card className="p-4 bg-blue-50 border-blue-100" data-id="element-63">
              <p className="text-blue-700 text-sm text-center" data-id="element-64">
                💡 提示：仔细听，大调和弦听起来更"亮"，小调和弦听起来更"暗"
              </p>
            </Card>
          </div>;
      case 'emotion':
        const emotionContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-65">
            <Card className="p-6 bg-pink-50 border-pink-100" data-id="element-66">
              <p className="text-pink-700 font-medium text-center" data-id="element-67">
                {emotionContent.instruction}
              </p>
            </Card>

            <div className="grid grid-cols-2 gap-4" data-id="element-68">
              {emotionContent.emotions.map((emotion: any, i: number) => <motion.button key={i} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => playMelody(emotion.melody, 300)} className={`p-6 rounded-2xl ${emotion.type === 'major' ? 'bg-amber-50 border-amber-200' : 'bg-purple-50 border-purple-200'} border-2 text-center transition-all hover:shadow-lg`} data-id="element-69">
                  <div className="text-5xl mb-3" data-id="element-70">{emotion.emoji}</div>
                  <h4 className="font-bold text-lg mb-1" data-id="element-71">{emotion.name}</h4>
                  <p className="text-xs text-slate-500" data-id="element-72">{emotion.desc}</p>
                </motion.button>)}
            </div>
          </div>;
      case 'quiz':
        const quizContent = currentLesson.content as any;
        const currentQuiz = quizContent.quizzes[quizIndex];
        return <div className="space-y-6" data-id="element-73">
            <Card className="p-6 bg-white" data-id="element-74">
              <p className="text-slate-700 font-medium text-center mb-6" data-id="element-75">
                {quizContent.question}
              </p>

              <div className="flex justify-center mb-6" data-id="element-76">
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => playMelody(currentQuiz.melody, 350)} className="px-8 py-4 bg-orange-100 text-orange-700 rounded-xl font-bold hover:bg-orange-200 flex items-center" data-id="element-77">
                  <Volume2 className="mr-2" size={20} data-id="element-78" />
                  播放第 {quizIndex + 1} 题
                </motion.button>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-79">
                {[{
                icon: <Sun size={32} data-id="element-80" />,
                name: '大调',
                desc: '明亮开心',
                color: 'amber'
              }, {
                icon: <Moon size={32} data-id="element-81" />,
                name: '小调',
                desc: '柔和忧伤',
                color: 'purple'
              }].map((option, i) => <button key={i} onClick={() => {
                if (!showResult) {
                  setSelectedAnswer(i);
                  setShowResult(true);
                  if (i === currentQuiz.answer) {
                    setScore(s => s + 10);
                  }
                }
              }} disabled={showResult} className={`p-6 rounded-xl border-2 flex flex-col items-center transition-all ${showResult ? i === currentQuiz.answer ? 'border-green-500 bg-green-50' : selectedAnswer === i ? 'border-red-500 bg-red-50' : 'border-slate-200 opacity-50' : `border-slate-200 hover:border-${option.color}-300 hover:bg-${option.color}-50`}`} data-id="element-82">
                    <div className={showResult && i === currentQuiz.answer ? 'text-green-600' : `text-${option.color}-500`} data-id="element-83">
                      {option.icon}
                    </div>
                    <span className="font-bold mt-2" data-id="element-84">{option.name}</span>
                    <span className="text-xs text-slate-500" data-id="element-85">
                      {option.desc}
                    </span>
                  </button>)}
              </div>
            </Card>

            {showResult && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-86">
                <Card className={`p-6 ${selectedAnswer === currentQuiz.answer ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-87">
                  <h4 className={`font-bold mb-2 ${selectedAnswer === currentQuiz.answer ? 'text-green-800' : 'text-amber-800'}`} data-id="element-88">
                    {selectedAnswer === currentQuiz.answer ? '🎉 回答正确！' : '💪 继续加油！'}
                  </h4>
                  <p className={selectedAnswer === currentQuiz.answer ? 'text-green-700' : 'text-amber-700'} data-id="element-89">
                    这是一段{currentQuiz.type === 'major' ? '大调' : '小调'}
                    旋律，听起来
                    {currentQuiz.type === 'major' ? '明亮开心' : '柔和忧伤'}。
                  </p>
                  {quizIndex < quizContent.quizzes.length - 1 && <button onClick={() => {
                setQuizIndex(quizIndex + 1);
                setSelectedAnswer(null);
                setShowResult(false);
              }} className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg font-bold" data-id="element-90">
                      下一题
                    </button>}
                </Card>
              </motion.div>}
          </div>;
      case 'interactive':
        const interactiveContent = currentLesson.content as any;
        // Convert melody to minor by lowering the 3rd
        const majorMelody = interactiveContent.baseMelody;
        const minorMelody = majorMelody.map((freq: number) => freq === 329.63 ? 311.13 : freq // E to Eb
        );
        return <div className="space-y-6" data-id="element-91">
            <Card className="p-6 bg-slate-50 border-slate-100" data-id="element-92">
              <p className="text-slate-700 font-medium text-center" data-id="element-93">
                {interactiveContent.instruction}
              </p>
            </Card>

            {/* Mode Toggle */}
            <Card className={`p-8 transition-colors duration-500 ${mode === 'major' ? 'bg-amber-50' : 'bg-purple-900'}`} data-id="element-94">
              <div className="flex flex-col items-center space-y-6" data-id="element-95">
                <div onClick={() => setMode(mode === 'major' ? 'minor' : 'major')} className={`relative w-64 h-20 rounded-full cursor-pointer transition-colors duration-500 shadow-inner flex items-center px-2 ${mode === 'major' ? 'bg-amber-100' : 'bg-slate-800'}`} data-id="element-96">
                  <motion.div layout transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} className={`w-16 h-16 rounded-full shadow-lg flex items-center justify-center ${mode === 'major' ? 'bg-white ml-0' : 'bg-slate-700 ml-auto'}`} data-id="element-97">
                    {mode === 'major' ? <Sun size={28} className="text-amber-500" data-id="element-98" /> : <Moon size={28} className="text-purple-400" data-id="element-99" />}
                  </motion.div>
                </div>

                <div className="text-center" data-id="element-100">
                  <h3 className={`text-3xl font-bold mb-2 ${mode === 'major' ? 'text-amber-600' : 'text-purple-300'}`} data-id="element-101">
                    {mode === 'major' ? '大调 Major' : '小调 Minor'}
                  </h3>
                  <p className={mode === 'major' ? 'text-amber-700' : 'text-purple-400'} data-id="element-102">
                    {mode === 'major' ? '☀️ 明亮 · 开心 · 阳光' : '🌙 柔和 · 忧伤 · 神秘'}
                  </p>
                </div>

                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => playMelody(mode === 'major' ? majorMelody : minorMelody, 350)} className={`px-8 py-4 rounded-full font-bold text-lg shadow-lg ${mode === 'major' ? 'bg-amber-500 text-white' : 'bg-purple-600 text-white'}`} data-id="element-103">
                  <Play className="inline mr-2" fill="currentColor" size={20} data-id="element-104" />
                  播放{mode === 'major' ? '大调' : '小调'}旋律
                </motion.button>
              </div>
            </Card>

            <Card className="p-4 bg-blue-50 border-blue-100" data-id="element-105">
              <p className="text-blue-700 text-sm text-center" data-id="element-106">
                💡 切换开关，听听同一段旋律在大调和小调下的不同感觉！
              </p>
            </Card>
          </div>;
      case 'summary':
        const summaryContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-107">
            <Card className="p-6 bg-gradient-to-br from-amber-50 to-purple-50" data-id="element-108">
              <div className="text-center mb-6" data-id="element-109">
                <div className="text-6xl mb-4" data-id="element-110">🎓</div>
                <h3 className="text-2xl font-bold text-slate-800" data-id="element-111">
                  恭喜完成学习！
                </h3>
                <p className="text-slate-500" data-id="element-112">
                  你已经掌握了大调和小调的基础知识
                </p>
              </div>

              <div className="space-y-4" data-id="element-113">
                {summaryContent.points.map((point: any, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.15
              }} className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm" data-id="element-114">
                    <span className="text-2xl" data-id="element-115">{point.emoji}</span>
                    <span className="text-slate-700 font-medium" data-id="element-116">
                      {point.text}
                    </span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-200" data-id="element-117">
              <div className="flex items-center justify-between" data-id="element-118">
                <div data-id="element-119">
                  <h4 className="font-bold text-amber-800" data-id="element-120">本次学习得分</h4>
                  <p className="text-amber-600 text-sm" data-id="element-121">
                    继续努力，争取更高分！
                  </p>
                </div>
                <div className="flex items-center space-x-2" data-id="element-122">
                  <Star className="text-amber-500" fill="currentColor" data-id="element-123" />
                  <span className="text-3xl font-bold text-amber-700" data-id="element-124">
                    {score}
                  </span>
                </div>
              </div>
            </Card>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full flex flex-col bg-slate-50" data-id="element-125">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-126">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-127">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 bg-slate-50 px-4 py-2 rounded-full" data-id="element-128">
            <ArrowLeft size={20} data-id="element-129" />
            <span className="font-bold text-sm" data-id="element-130">返回</span>
          </button>

          <h1 className="text-xl font-bold text-slate-900" data-id="element-131">情绪调色盘</h1>

          <div className="flex items-center space-x-2 bg-amber-50 px-4 py-2 rounded-full" data-id="element-132">
            <Star className="text-amber-500" size={18} fill="currentColor" data-id="element-133" />
            <span className="font-bold text-amber-700" data-id="element-134">{score}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto px-4 pb-4" data-id="element-135">
          <div className="flex items-center space-x-2" data-id="element-136">
            {lessonSteps.map((step, i) => <div key={i} className="flex-1" data-id="element-137">
                <div className={`h-2 rounded-full ${completedSteps.includes(i) ? 'bg-orange-500' : i === currentStep ? 'bg-orange-400' : 'bg-slate-200'}`} data-id="element-138" />
              </div>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8" data-id="element-139">
        <div className="max-w-2xl mx-auto" data-id="element-140">
          {/* Step Header */}
          <div className="mb-6 text-center" data-id="element-141">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4" data-id="element-142">
              <span className="text-2xl" data-id="element-143">{currentLesson.icon}</span>
              <span className="text-sm font-medium text-slate-500" data-id="element-144">
                第 {currentStep + 1} 步 / 共 {lessonSteps.length} 步
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-145">
              {currentLesson.title}
            </h2>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait" data-id="element-146">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-147">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-slate-100 p-4" data-id="element-148">
        <div className="max-w-2xl mx-auto flex justify-between items-center" data-id="element-149">
          <button onClick={() => {
          setSelectedAnswer(null);
          setShowResult(false);
          if (currentStep > 0) setCurrentStep(currentStep - 1);
        }} disabled={currentStep === 0} className="px-6 py-2 rounded-full border border-slate-200 text-slate-600 font-medium disabled:opacity-50" data-id="element-150">
            上一步
          </button>

          <button onClick={currentStep === lessonSteps.length - 1 ? onBack : handleNextStep} className="px-8 py-2 rounded-full bg-orange-600 text-white font-bold shadow-md hover:bg-orange-700" data-id="element-151">
            {currentStep === lessonSteps.length - 1 ? '完成学习' : '下一步'}
            {currentStep < lessonSteps.length - 1 && <ChevronRight className="inline ml-1" size={18} data-id="element-152" />}
          </button>
        </div>
      </div>
    </motion.div>;
}